<template>
  <div>
    <CRow>
      <CCol sm="6">
        <div class="card-title mb-3">
          {{ $t("role.planning.market.title") }}
        </div>
      </CCol>
      <CCol sm="6" class="justify-content-end d-flex">
        <div>
          <CButton color="primary" class="mb-3" @click="addRule()">
            <font-awesome-icon icon="plus" class="mr-2" />
            {{ $t("role.planning.market.addRule") }}
          </CButton>
        </div>
      </CCol>
    </CRow>

    <CRow v-if="planningData && planningData.length > 0" class="p-3">
      <Header
        :data="[
          $t('models.market_plan.name'),
          $t('models.market_plan.rule'),
          $t('models.market_plan.order_close_time'),
          $t('models.market_plan.offset_producer_close'),
          $t('models.market_plan.offset_producer_delivery'),
          $t('models.market_plan.offset_order_delivery'),
          'Actions',
        ]"
      />
      <Row
        v-for="(item, index) in planningData"
        :key="index"
        :data="[
          { data: ruleName(item.name), type: 'html' },
          { data: decodeRule(item.order_close), type: 'html' },
          { data: closingTime(item.order_close_time), type: 'html' },
          {
            data: offset(item.offset_producer_close, item.order_close_time),
            type: 'html',
          },
          {
            data: offset(item.offset_producer_delivery, item.order_close_time),
            type: 'html',
          },
          {
            data: offset(item.offset_order_delivery, item.order_close_time),
            type: 'html',
          },
          { data: '', type: 'actions' },
        ]"
        :border="true"
        :topAligned="true"
      >
        <template #actions>
          <div class="d-flex justify-content-end">
            <CButton
              class="mr-2 mt-2"
              size="sm"
              color="primary"
              @click="editPlanning(item)"
            >
              <font-awesome-icon icon="edit" />
            </CButton>
            <CButton
              v-if="item.order_close && item.order_close.startsWith('RRULE:')"
              class="mr-2 mt-2"
              size="sm"
              color="primary"
              @click="manageExceptions(item)"
            >
              <font-awesome-icon icon="exclamation-triangle" />
            </CButton>
            <CButton
              class="mr-2 mt-2"
              size="sm"
              color="primary"
              @click="showDeleteConfirmation(item.id)"
            >
              <font-awesome-icon icon="trash-alt" />
            </CButton>
          </div>
        </template>
      </Row>
    </CRow>

    <PlanningModal
      :show.sync="modal.show"
      :title="modal.title"
      :values="modal.values"
      :edit-mode="modal.editMode"
      :edit-id="modal.editId"
      @submitted="fetchData"
    />

    <ExceptionsModal
      :show.sync="exceptionsModal.show"
      :title="exceptionsModal.title"
      :planning-id="exceptionsModal.planningId"
      :current-rule="exceptionsModal.currentRule"
      @saved="fetchData"
    />

    <CModal
      :title="$t('role.planning.closing_days.modal.title')"
      :show.sync="deleteModal.show"
      :closeOnBackdrop="false"
      :centered="true"
    >
      <p>
        {{ $t("role.planning.closing_days.modal.subtitle") }}
      </p>
      <p>
        {{ $t("role.planning.closing_days.modal.text") }}
      </p>
      <template #footer>
        <CButton color="primary" @click="onModalConfirm">{{
          $t("common.editor.button.confirm")
        }}</CButton>
        <CButton color="primary" variant="outline" @click="onModalClose">{{
          $t("common.editor.button.cancel")
        }}</CButton>
      </template>
    </CModal>
  </div>
</template>
<script>
import { RRule, rrulestr } from "rrule";
import PlanningModal from "./PlanningModal.vue";
import ExceptionsModal from "./ExceptionsModal.vue";
import Header from "../../../common/table/Header.vue";
import Row from "../../../common/table/Row.vue";
import { apiDelete, apiGet } from "../../../helpers/apiHelper";

export default {
  components: {
    PlanningModal,
    ExceptionsModal,
    Row,
    Header,
  },

  data() {
    return {
      planningData: [],
      modal: {
        show: false,
        title: this.$t("models.market_plan.addForm.modalTitle"),
        values: {},
        editMode: false,
        editId: null,
      },
      exceptionsModal: {
        show: false,
        title: this.$t("models.market_plan.exceptions.modalTitle"),
        planningId: null,
        currentRule: "",
      },
      deleteModal: {
        show: false,
        planningIdToDelete: null,
      },
    };
  },

  methods: {
    decodeRule(ruleString) {
      if (!ruleString) return "No rule defined";

      try {
        // Handle RDATE (specific dates)
        if (ruleString.startsWith("RDATE:")) {
          const lines = ruleString
            .split("\n")
            .filter((line) => line.startsWith("RDATE:"));
          const dates = lines.map((line) => {
            const dateStr = line.replace("RDATE:", "");
            const date = new Date(
              dateStr.replace(
                /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/,
                "$1-$2-$3T$4:$5:$6Z"
              )
            );
            return date.toLocaleDateString();
          });
          return `<strong>${this.$t(
            "models.market_plan.addForm.specificLabel"
          )}:</strong><br><span style="font-size: 0.75rem;">${dates.join(
            ", "
          )}</span>`;
        }

        // Handle RRULE (recurring rules)
        if (ruleString.startsWith("RRULE:")) {
          const rrule = rrulestr(ruleString.split("\n")[0]);
          const options = rrule.origOptions;

          let parts = [
            `<strong>${this.$t(
              "models.market_plan.addForm.recurringLabel"
            )}:</strong><span style="font-size: 0.75rem;">`,
          ];

          // Frequency and interval
          if (options.freq === RRule.WEEKLY) {
            const interval = options.interval || 1;
            parts.push(
              `${this.$t("models.market_plan.addForm.frequency")}: ${this.$t(
                "models.market_plan.addForm.weeklyLabel"
              )}<br> ${this.$t(
                "models.market_plan.addForm.interval"
              )}: ${interval}`
            );

            // Days of week
            if (options.byweekday && options.byweekday.length > 0) {
              const dayNames = options.byweekday.map((day) => {
                const dayMap = {
                  0: this.$t("common.days.mon"),
                  1: this.$t("common.days.tue"),
                  2: this.$t("common.days.wed"),
                  3: this.$t("common.days.thu"),
                  4: this.$t("common.days.fri"),
                  5: this.$t("common.days.sat"),
                  6: this.$t("common.days.sun"),
                };
                return dayMap[day.weekday];
              });
              parts.push(
                `${this.$t("models.market_plan.addForm.days")}: ${dayNames.join(
                  ", "
                )}`
              );
            }
          } else if (options.freq === RRule.MONTHLY) {
            const interval = options.interval || 1;
            parts.push(
              `${this.$t("models.market_plan.addForm.frequency")}: ${this.$t(
                "models.market_plan.addForm.monthlyLabel"
              )}<br> ${this.$t(
                "models.market_plan.addForm.interval"
              )}: ${interval}`
            );
            console.log(options);

            // Days of month
            if (options.bymonthday) {
              const daysOfMonth = Array.isArray(options.bymonthday)
                ? options.bymonthday
                : [options.bymonthday];
              parts.push(
                `${this.$t(
                  "models.market_plan.addForm.daysOfMonth"
                )}: ${daysOfMonth.join(", ")}`
              );
            }
          }

          // End date
          if (options.until) {
            const untilDate = new Date(options.until).toLocaleDateString();
            parts.push(
              `${this.$t("models.market_plan.addForm.endDate")}: ${untilDate}`
            );
          }

          parts.push("</span>");

          let result = parts.join("<br>");

          // Check for exceptions
          const exceptions = this.getExceptions(ruleString);
          if (exceptions.length > 0) {
            result += `<div class="alert alert-warning mt-2 mb-0" style="font-size: 0.75rem; padding: 0.5rem;"><strong>${this.$t(
              "models.market_plan.exceptions.existingDates"
            )}:</strong> ${exceptions.join(", ")}</div>`;
          }

          return result;
        }
      } catch (e) {
        console.error("Error decoding rule:", e);
        return "Invalid rule format";
      }
    },

    getExceptions(ruleString) {
      if (!ruleString) return [];

      const exceptions = [];
      const lines = ruleString.split("\n");
      lines.forEach((line) => {
        if (line.startsWith("EXDATE:")) {
          const dateStr = line.replace("EXDATE:", "");
          const date = new Date(
            dateStr.replace(
              /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/,
              "$1-$2-$3T$4:$5:$6Z"
            )
          );
          exceptions.push(date.toLocaleDateString());
        }
      });
      return exceptions;
    },

    ruleName(item) {
      return `<strong>${item}</strong>`;
    },

    calculateOffsetDay(value, orderClosingTime) {
      const days = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];

      const daysPassed = value / 24;
      const fullDaysPassed = Math.floor(daysPassed);
      const hoursAfterFullDaysPassed = (daysPassed - fullDaysPassed) * 24;
      const hoursLeftInDay = 24 - parseInt(orderClosingTime.split(":")[0]);
      let offsetDays = fullDaysPassed;

      if (hoursAfterFullDaysPassed >= hoursLeftInDay) {
        offsetDays += 1;
      }

      return days[offsetDays];
    },

    offset(value, orderClosingTime) {
      const day = this.calculateOffsetDay(value, orderClosingTime);
      const offsetDayHours = value % 24;

      const [closeHours, closeMinutes, closeSeconds] = orderClosingTime
        .split(":")
        .map(Number);
      const totalMinutes = closeHours * 60 + closeMinutes + offsetDayHours * 60;
      const totalSeconds =
        closeHours * 3600 + closeMinutes * 60 + offsetDayHours * 3600;
      const newHours = Math.floor(totalMinutes / 60) % 24;
      const newMinutes = totalMinutes % 60;
      const newSeconds = totalSeconds % 60;

      const timeOfDay = `${String(newHours).padStart(2, "0")}:${String(
        newMinutes
      ).padStart(2, "0")}:${String(newSeconds).padStart(2, "0")}`;

      const rotateHours =
        newHours > 12
          ? (newHours - 12 - newMinutes / 60) / 12
          : (newHours - newMinutes / 60) / 12;
      const rotateMinutes = newMinutes / 60;

      return `
      <div style="display: block; width:100%; padding: 5px; border-radius: 3px;">
        <div style="font-size: 12px; border-radius:5px; background-color: #e3e3e3; color: #09838f; border-top: 6px solid #09838f; font-weight: bold; font-size:14px; width: 25px; height: 25px; display: inline-block; text-align: center; line-height: 17px; margin-left: 5px; margin-right: 10px;">${day}</div> ${this.$t(
        "models.market_plan.dayLabel"
      )} ${day}
      </div>
      <div style="display: flex; justify-content: flex-start; align-items: center; flex-direction: row; width:100%; padding: 5px; border-radius: 3px;">
        <div style="position: relative; font-size: 12px; border-radius:100px; background-color: #e3e3e3; color: #09838f; font-weight: bold; font-size:14px; width: 25px; height: 25px; display: inline-block; text-align: center; line-height: 17px; margin-left: 5px; margin-right: 10px;">
          <span style="display: block; position: absolute; height: 7.5px; width:2px; background-color: #09838f; left: 50%; top: 5px; transform: translateX(-50%) rotate(${rotateHours}turn); transform-origin: center 100%;"></span>
          <span style="display: block; position: absolute; height: 10px; width:2px; background-color: #09838f; left: 50%; top: 2px; transform: translateX(-50%) rotate(${rotateMinutes}turn); transform-origin: center 100%;"></span>
          </div>
      ${this.$t(
        "role.logistics.Planning.calendar.at"
      )} ${timeOfDay}</div><span style="font-style: italic; font-size: 0.75rem; display: block; margin-left: 45px;">${value} ${this.$t(
        "models.market_plan.hoursAfter"
      )}</span>`;
    },

    closingTime(value) {
      const [closeHours, closeMinutes, closeSeconds] = value
        .split(":")
        .map(Number);

      const rotateHours =
        closeHours > 12
          ? (closeHours - 12 - closeMinutes / 60) / 12
          : (closeHours - closeMinutes / 60) / 12;
      const rotateMinutes = closeMinutes / 60;

      return `
      <div style="display: block; width:100%; padding: 5px; border-radius: 3px;">
        <div style="font-size: 12px; border-radius:5px; background-color: #e3e3e3; color: #09838f; border: 0px solid #09838f; border-top: 6px solid #09838f; font-weight: bold; font-size:14px; width: 25px; height: 25px; display: inline-block; text-align: center; line-height: 17px; margin-left: 5px; margin-right: 10px;">A</div> ${this.$t(
          "models.market_plan.dayLabel"
        )} A
      </div>
      <div style="display: flex; justify-content: flex-start; align-items: center; flex-direction: row; width:100%; padding: 5px; border-radius: 3px;">
        <div style="position: relative; font-size: 12px; border-radius:100px; background-color: #e3e3e3; color: #09838f; border: 0px solid #09838f; font-weight: bold; font-size:14px; width: 25px; height: 25px; display: inline-block; text-align: center; line-height: 17px; margin-left: 5px; margin-right: 10px;">
          <span style="display: block; position: absolute; height: 7.5px; width:2px; background-color: #09838f; left: 50%; top: 5px; transform: translateX(-50%) rotate(${rotateHours}turn); transform-origin: center 100%;"></span>
          <span style="display: block; position: absolute; height: 10px; width:2px; background-color: #09838f; left: 50%; top: 2px; transform: translateX(-50%) rotate(${rotateMinutes}turn); transform-origin: center 100%;"></span>
          </div>
      ${this.$t("role.logistics.Planning.calendar.at")} ${value}</div>`;
    },

    addRule() {
      this.modal.title = this.$t("models.market_plan.addForm.modalTitle");
      this.modal.values = {};
      this.modal.editMode = false;
      this.modal.editId = null;
      this.modal.show = true;
    },

    editPlanning(item) {
      this.modal.title = this.$t("common.editor.button.edit");
      this.modal.values = item;
      this.modal.editMode = true;
      this.modal.editId = item.id;
      this.modal.show = true;
    },

    manageExceptions(item) {
      this.exceptionsModal.title = this.$t(
        "models.market_plan.exceptions.modalTitle"
      );
      this.exceptionsModal.planningId = item.id;
      this.exceptionsModal.currentRule = item.order_close;
      this.exceptionsModal.show = true;
    },

    async fetchData() {
      const response = await apiGet(
        "role_market_planningdata",
        [this.$store.state.role.id],
        null
      );
      if (response && response.status === 200) {
        this.planningData = response.data.Items.sort((a, b) => a.id - b.id);
      } else {
        this.$store.dispatch("toaster/add", {
          title: this.$t("role.finance.invoicesSettings.attention"),
          text: "Error loading planning data",
          color: "danger",
          autohide: true,
        });
      }
    },

    showDeleteConfirmation(id) {
      this.deleteModal.planningIdToDelete = id;
      this.deleteModal.show = true;
    },

    onModalConfirm() {
      this.deleteModal.show = false;
      this.removePlanning(this.deleteModal.planningIdToDelete);
    },

    onModalClose() {
      this.deleteModal.show = false;
      this.deleteModal.planningIdToDelete = null;
    },

    async removePlanning(id) {
      const response = await apiDelete("role_market_planningdata", id, [
        this.$store.state.role.id,
      ]);
      if (response && response.status === 204) {
        this.$store.dispatch("toaster/add", {
          title: "OK!",
          text: this.$t("models.market_plan.removedMsg"),
          color: "success",
          autohide: true,
        });
        await this.fetchData();
      } else {
        this.$store.dispatch("toaster/add", {
          title: this.$t("role.finance.invoicesSettings.attention"),
          text: "Error removing planning rule",
          color: "danger",
          autohide: true,
        });
      }
    },
  },

  async created() {
    await this.fetchData();
  },
};
</script>
